import { useRef, useState, useCallback } from 'react'

/** Manages cart quantity delta changes for Cart UI and BE quantity update calls. useState triggers UI re-renders to show pending changes immediately. useRef provides a stable reference to values for debounced functions */
export const useQuantityDeltas = () => {
  const [pendingDeltas, setPendingDeltas] = useState<Record<string, number>>({})
  const pendingDeltasRef = useRef<Record<string, number>>({})

  /** Adds a delta to the pending changes for a specific item. Updates both the ref (for debounced access) and state (for UI updates) */
  const updatePendingDelta = useCallback(
    (id: string, delta: number, currentQuantity: number) => {
      const currentDelta = pendingDeltasRef.current[id] || 0
      const newDelta = currentDelta + delta
      const finalQuantity = currentQuantity + newDelta

      // Only update if the final quantity (original + delta) would not be negative
      if (finalQuantity >= 0) {
        pendingDeltasRef.current[id] = newDelta
        setPendingDeltas((prev) => ({
          ...prev,
          [id]: pendingDeltasRef.current[id]
        }))
      }
    },
    []
  )

  /** Removes a pending delta after it's been successfully processed. Clears from both ref and state to keep them synchronized */
  const clearPendingDelta = useCallback((id: string) => {
    delete pendingDeltasRef.current[id]
    setPendingDeltas((prev) => {
      const newDeltas = { ...prev }
      delete newDeltas[id]
      return newDeltas
    })
  }, [])

  /** Gets the current pending delta for an item. Uses ref to ensure debounced functions always get the most current value */
  const getPendingDelta = useCallback(
    (id: string) => pendingDeltasRef.current[id] || 0,
    []
  )

  /** Resets the pending delta to a specific value (used when backend errors occur) */
  const resetPendingDelta = useCallback((id: string, value: number = 0) => {
    pendingDeltasRef.current[id] = value
    setPendingDeltas((prev) => ({
      ...prev,
      [id]: value
    }))
  }, [])

  return {
    pendingDeltas,
    updatePendingDelta,
    clearPendingDelta,
    getPendingDelta,
    resetPendingDelta
  }
}
